import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { useSweepParams } from '../navigation-utils/useSweepParams';
import { replaceOrgInPath } from '../navigation-utils/urlOrgUtils';
import useSweepNavigate from '../navigation-utils/useSweepNavigate';
import useUserInfo from './useUserInfo';
import { clearSelectedAdditionalOrgIds } from '../components/aiAgentCenter/agentsHomeReducer';
import { useSelector } from 'react-redux';
import { selectUserAccountId } from '../reducers/userInfoReducer';

/**
 * Hook to change environment without race conditions
 *
 * Implements the URL-as-SSOT pattern:
 * - Case 1: If current route has crmOrgId → only update URL (let useCrmOrgFromUrl handle state sync)
 * - Case 2: If current route has no crmOrgId → only update state (no navigation needed)
 *
 * This eliminates race conditions by ensuring only one update happens at a time.
 */
export const useChangeEnvironment = () => {
  const { pathname, hash, search } = useLocation();
  const { fullCrmOrgId, crmOrgId } = useSweepParams<{ crmOrgId: string }>(); // Full UUID for business logic
  const { navigate } = useSweepNavigate();
  const { updateDefaultCreationCrmOrgId } = useUserInfo();
  const dispatch = useDispatch();
  const userAccountId = useSelector(selectUserAccountId);

  const navigateToEnv = useCallback(
    (targetCrmOrgId: string) => {
      if (!targetCrmOrgId || !fullCrmOrgId || !crmOrgId || !userAccountId) return;
      if (targetCrmOrgId === fullCrmOrgId) return;

      // Use raw URL param for URL manipulation, not the converted one
      let newPath = replaceOrgInPath(pathname, crmOrgId, targetCrmOrgId, userAccountId);

      // TEMP FIX: If on an agent route, strip agent/chat params and go to agentic root.
      // Agent from old org won't exist in new org, causing infinite loader.
      // TODO: Remove once agentsChats state is refactored to be per-environment SWEEP-8591
      const agentRouteMatch = newPath.match(/^(\/agentic\/[^/]+\/[^/]+)\/agent\/.*/);
      if (agentRouteMatch) {
        newPath = agentRouteMatch[1];
      }

      const fullPath = `${newPath}${search}${hash}`;
      navigate(fullPath);
    },
    [fullCrmOrgId, crmOrgId, hash, navigate, pathname, search, userAccountId],
  );

  const changeEnvironment = useCallback(
    (targetCrmOrgId: string | null) => {
      // Clear multi-org selection when environment changes
      dispatch(clearSelectedAdditionalOrgIds());

      if (fullCrmOrgId && targetCrmOrgId) {
        // Case 1: Current route has crmOrgId and we have a target → only update URL
        // useCrmOrgFromUrl will handle state synchronization
        navigateToEnv(targetCrmOrgId);
      } else {
        // Case 2: Current route has no crmOrgId OR targetCrmOrgId is null → only update state
        // No navigation needed since route doesn't support org ID or we're clearing the org
        updateDefaultCreationCrmOrgId(targetCrmOrgId);
      }
    },
    [dispatch, fullCrmOrgId, navigateToEnv, updateDefaultCreationCrmOrgId],
  );

  return { changeEnvironment };
};
